import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw, Settings } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { PageHeader } from "@/components/PageHeader";
import { ProjectsApi, type ProjectCreatePayload } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CreateProjectDialog } from "./CreateProjectDialog";

export function ProjectsPage() {
  const [showCreate, setShowCreate] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isAdmin = !!useAuth((s) => s.user?.is_superuser);

  const projects = useQuery({ queryKey: ["projects"], queryFn: ProjectsApi.list });

  const createProject = useMutation({
    mutationFn: (payload: ProjectCreatePayload) => ProjectsApi.create(payload),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      setShowCreate(false);
      navigate(`/projects/${created.id}/tasks`);
    },
  });

  return (
    <>
      <PageHeader
        title="Projects"
        subtitle="Audio labeling projects you own or contribute to."
        actions={
          <>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => projects.refetch()}
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            {isAdmin && (
              <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
                <Plus size={16} />
                New project
              </button>
            )}
          </>
        }
      />

      <div className="px-4 py-4 md:px-8 md:py-6">
        <div className="card overflow-hidden">
          {projects.isLoading && <div className="p-6 text-sm text-text-muted">Loading…</div>}
          {projects.isError && (
            <div className="p-6 text-sm text-status-error">
              Failed to load projects. Are you signed in?
            </div>
          )}
          {projects.data?.length === 0 && (
            <div className="p-10 text-center text-sm text-text-muted">
              {isAdmin ? (
                <>No projects yet. Click <span className="text-text">New project</span> to create one.</>
              ) : (
                <>You haven't been added to any projects yet. Ask a project admin to add you.</>
              )}
            </div>
          )}

          {/* Desktop table — unchanged at md+. */}
          <div className="hidden md:block">
            {(projects.data?.length ?? 0) > 0 && (
              <div className="data-row grid-cols-[minmax(0,2fr)_minmax(0,3fr)_1fr_1.2fr_1fr_1fr] text-xs font-semibold uppercase tracking-wider text-text-dim">
                <div>Name</div>
                <div>S3</div>
                <div>Tags</div>
                <div>Tasks</div>
                <div>Created</div>
                <div className="text-right">Actions</div>
              </div>
            )}
            {projects.data?.map((p) => (
              <div
                key={p.id}
                className="data-row grid-cols-[minmax(0,2fr)_minmax(0,3fr)_1fr_1.2fr_1fr_1fr] cursor-pointer"
                onClick={() => navigate(`/projects/${p.id}/tasks`)}
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">{p.name}</div>
                  {p.description && (
                    <div className="truncate text-xs text-text-dim">{p.description}</div>
                  )}
                </div>
                <div className="min-w-0 font-mono text-xs text-text-muted">
                  s3://{p.s3_bucket}/{p.s3_prefix}
                </div>
                <div className="text-xs text-text-muted">{p.tag_schema.length} labels</div>
                <div className="text-xs">
                  {p.total_tasks === 0 ? (
                    <span className="text-text-dim">empty</span>
                  ) : (
                    <>
                      <span className={p.tagged_tasks === p.total_tasks ? "text-accent" : "text-text"}>
                        {p.tagged_tasks}
                      </span>
                      <span className="text-text-dim"> / {p.total_tasks}</span>
                      <span className="ml-1 text-text-dim">
                        ({Math.round((p.tagged_tasks / p.total_tasks) * 100)}%)
                      </span>
                      {p.my_submitted_tasks > 0 && (
                        <span className="ml-2 rounded-md bg-accent/15 px-1.5 py-0.5 text-[11px] font-medium text-accent">
                          me: {p.my_submitted_tasks}
                        </span>
                      )}
                    </>
                  )}
                </div>
                <div className="text-xs text-text-muted">
                  {new Date(p.created_at).toLocaleDateString()}
                </div>
                <div className="text-right">
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/projects/${p.id}/settings`);
                      }}
                      className="rounded p-1.5 text-text-muted hover:bg-bg-elevated hover:text-text"
                      title="Project settings"
                      aria-label="Project settings"
                    >
                      <Settings size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Mobile cards. */}
          <div className="divide-y divide-border-muted md:hidden">
            {projects.data?.map((p) => (
              <div
                key={p.id}
                onClick={() => navigate(`/projects/${p.id}/tasks`)}
                className="flex flex-col gap-2 px-4 py-3 active:bg-bg-elevated"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{p.name}</div>
                    {p.description && (
                      <div className="truncate text-xs text-text-dim">{p.description}</div>
                    )}
                  </div>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/projects/${p.id}/settings`);
                      }}
                      className="-mr-1 shrink-0 rounded p-2 text-text-muted hover:bg-bg-elevated hover:text-text"
                      title="Project settings"
                      aria-label="Project settings"
                    >
                      <Settings size={18} />
                    </button>
                  )}
                </div>
                <div className="truncate font-mono text-[11px] text-text-muted">
                  s3://{p.s3_bucket}/{p.s3_prefix}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
                  <span>{p.tag_schema.length} labels</span>
                  {p.total_tasks === 0 ? (
                    <span className="text-text-dim">· empty</span>
                  ) : (
                    <>
                      <span>
                        · <span className={p.tagged_tasks === p.total_tasks ? "text-accent" : "text-text"}>{p.tagged_tasks}</span>
                        <span className="text-text-dim"> / {p.total_tasks} tagged</span>
                      </span>
                      {p.my_submitted_tasks > 0 && (
                        <span className="rounded-md bg-accent/15 px-1.5 py-0.5 text-[11px] font-medium text-accent">
                          me: {p.my_submitted_tasks}
                        </span>
                      )}
                    </>
                  )}
                  <span className="text-text-dim">· {new Date(p.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showCreate && (
        <CreateProjectDialog
          onClose={() => setShowCreate(false)}
          onSubmit={(payload) => createProject.mutate(payload)}
          submitting={createProject.isPending}
          error={
            (createProject.error as { response?: { data?: { detail?: string } } } | undefined)?.response?.data?.detail
          }
        />
      )}
    </>
  );
}
